import { useState, useRef } from 'react';
import classNames from './ClosedSpellingDictionary.module.scss';
import SVG from 'react-inlinesvg';

import SectionSeperator from '@/components/SectionSeperator/SectionSeperator';
import classes from 'classnames';
import soundPlay from '@/assets/svg/speaker.svg';
import whiteSoundPlay from '@/assets/svg/white_speaker.svg';
import UseScreenSize from '@/hooks/UseScreenSize';
import commonUtils from '@/utils/common';
import { useAppSelector } from '@/store';
import { interfaceLanguageSelector } from '@/store/reducers/auth';
import { useTranslation } from 'react-i18next';
import { authSelector } from '@/store/reducers/auth';
import UseUnitsAssets from '@/hooks/UseUnitsAssets';
import { unitSelector } from '@/store/reducers/unit';

interface ClosedSpellingDictionaryProps {
  answer: string;
  partOfSpeech: string;
  translation: string;
  exampleSentences: string[];
  soundPath: string;
}

const ClosedSpellingDictionary = ({
  answer,
  partOfSpeech,
  translation,
  exampleSentences,
  soundPath,
}: ClosedSpellingDictionaryProps) => {
  const { t } = useTranslation();
  const { isMobile } = UseScreenSize();
  const interfaceLanguage = useAppSelector(interfaceLanguageSelector);
  const { nativeLanguage } = useAppSelector(authSelector);
  const { unitsAssets } = UseUnitsAssets();
  const { selectedUnit } = useAppSelector(unitSelector);
  const unitColor = unitsAssets[selectedUnit?.order || 1].modalBorderColor;
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const onPlaySound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }
    const audio = new Audio(soundPath);
    audioRef.current = audio;
    setIsPlaying(true);
    audio.play();
    audio.onended = () => {
      setIsPlaying(false);
      audioRef.current = null;
    };
  };
  const exampleSentencesElements = exampleSentences.map((exampleSentence, index) => {
    return (
      <div
        key={index}
        className={classNames.exampleSentence}
        dangerouslySetInnerHTML={{
          __html: commonUtils.formatText(exampleSentence),
        }}
      ></div>
    );
  });

  return (
    <div className={classNames.closedSpellingDictionary}>
      <div
        className={classes(classNames.top, {
          [classNames.isRtl]: interfaceLanguage?.direction === 'rtl',
        })}
      >
        <div className={classNames.text}>
          <span className={classNames.answer}>{answer}</span>
          <div className={classNames.partOfSpeech}>
            <span>{partOfSpeech.toLowerCase()}</span>
          </div>
        </div>
        {soundPath && (
          <button
            className={classNames.soundBtn}
            style={{ backgroundColor: isPlaying ? unitColor : undefined }}
            onClick={onPlaySound}
            aria-label={t('play_sound') || ''}
          >
            <SVG
              src={isPlaying ? whiteSoundPlay : soundPlay}
              color={isPlaying ? 'white' : undefined}
            />
          </button>
        )}
      </div>
      <div
        className={classes(classNames.content, {
          [classNames.contentRtl]: ['he', 'ar'].includes(nativeLanguage || ''),
        })}
      >
        <span className={classNames.translation}>{translation}</span>
        <SectionSeperator className={isMobile ? 'my-3' : 'my-6'} />
        <div className={classNames.exampleSentences}>{exampleSentencesElements}</div>
      </div>
    </div>
  );
};

export default ClosedSpellingDictionary;
